import { normalizeCostOverride } from "../../../../jobs/jobs-list/jobs-layout/components/Summary/BasicJob";
import type { ScopeOfWork } from "../../../types/scope-of-work.interface";
import { roundMoney } from "./calculateTotal";
import { getGrossLineFromCostOverride } from "./invoiceLineFromCostOverride";

export type InvoiceMainLineSeed = {
  servicePrice: number;
  timeMinutes: number;
  gross: number;
};

/**
 * Canonical main-line unit price, duration, and gross total for invoice edit (costOverride + job fallback).
 * Used to seed the row and to restore defaults on Reset.
 */
export function computeInvoiceMainLineSeed(
  scope: ScopeOfWork,
  invoiceLineGrossFallback: number | null | undefined,
): InvoiceMainLineSeed | null {
  const co = normalizeCostOverride(scope.costOverride) as Record<string, unknown> | null;
  const fromCo = getGrossLineFromCostOverride(co);
  const fallback =
    invoiceLineGrossFallback != null && Number(invoiceLineGrossFallback) > 0
      ? roundMoney(Number(invoiceLineGrossFallback))
      : null;
  const gross = fromCo != null && fromCo > 0 ? fromCo : fallback;
  if (gross == null || gross <= 0) return null;

  const rawUnit = co?.value ?? co?.price ?? co?.amount;
  const value = rawUnit != null && rawUnit !== "" ? Number(rawUnit) : null;
  const unitsParsed = co?.units != null && co.units !== "" ? Number(co.units) : NaN;
  const units = Number.isFinite(unitsParsed) && unitsParsed > 0 ? unitsParsed : null;

  const fromFallbackOnly = (fromCo == null || fromCo <= 0) && fallback != null && fallback > 0;

  let timeMinutes =
    units != null ? units * 60 : value != null && value > 0 ? 60 : 0;
  let servicePrice = value != null && value > 0 ? value : 0;

  if (fromFallbackOnly) {
    timeMinutes = 60;
    servicePrice = roundMoney(gross);
  }

  if (gross > 0 && timeMinutes <= 0) {
    timeMinutes = 60;
  }
  if (gross > 0 && servicePrice <= 0) {
    servicePrice = roundMoney(gross);
  }

  return { servicePrice, timeMinutes, gross };
}
